<script setup lang="ts">
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { Alert, AlertDescription } from '~/components/ui/alert'
import { AlertCircle } from 'lucide-vue-next'
import { Button } from '~/components/ui/button'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { useApiPost } from '~/composables/useApiPost'
import { WidgetPhoneInput } from '~/components/widget/phone-input'
import type { FormServerErrorResponse } from '~/types'
import { ANCHOR_IDS } from '~/lib/constants'
import type { TContentBlockVariant } from '~/components/ui/content-block'
import type { THeading } from '~/components/ui/heading'

const { t: _t } = useI18n()

// Pre-compute all validation messages to avoid calling useValidationMessage in computed
const validationMessages = {
  requiredFieldLastName: useValidationMessage('requiredFieldLastName'),
  minValue2: useValidationMessage('minValue', 2),
  maxValue30: useValidationMessage('maxValue', 30),
  requiredFieldFirstName: useValidationMessage('requiredFieldFirstName'),
  requiredFieldMessage: useValidationMessage('requiredFieldMessage'),
  minValue1: useValidationMessage('minValue', 1),
  maxValue500: useValidationMessage('maxValue', 500),
  requiredFieldEmail: useValidationMessage('requiredFieldEmail'),
  requiredEmailInvalid: useValidationMessage('requiredEmailInvalid'),
  agreement: useValidationMessage('agreement'),

}

const props = defineProps<{
  id: number
  type: string
  headingText?: string
  headingTag?: THeading
  headingClass?: string
  variant?: TContentBlockVariant
}>()

const config = useRuntimeConfig()
const { trackEvent } = useAnalytics()

// Built in setup: the phone schema resolves its messages through useI18n
const phoneSchema = usePhoneValidationSchema()

const formSchema = computed(() =>
  toTypedSchema(
    z.object({
      last_name: z
        .string({ message: validationMessages.requiredFieldLastName })
        .min(2, { message: validationMessages.minValue2 })
        .max(50, { message: validationMessages.maxValue30 }),
      first_name: z
        .string({ message: validationMessages.requiredFieldFirstName })
        .min(2, { message: validationMessages.minValue2 })
        .max(50, { message: validationMessages.maxValue30 }),
      middle_name: z.string().optional(),
      message: z
        .string({ message: validationMessages.requiredFieldMessage })
        .min(1, { message: validationMessages.minValue1 })
        .max(500, { message: validationMessages.maxValue500 }),
      phone: phoneSchema,
      email: z
        .string({ message: validationMessages.requiredFieldEmail })
        .email({ message: validationMessages.requiredEmailInvalid }),
      agreement: z
        .boolean({
          required_error: validationMessages.agreement
        })
        .refine((val) => val, {
          message: validationMessages.agreement
        })
    })
  )
)

const form = useForm({
  validationSchema: formSchema
})

const alert = ref(false)
const alertMessage = ref('')

const handleServerValidationErrors = <T extends string>(
  error: unknown,
  setFieldError: (field: T, message: string | string[] | undefined) => void,
  knownFields: T[]
) => {
  if (typeof error === 'object' && error !== null && '_data' in error) {
    const serverError = error as FormServerErrorResponse
    const errors = serverError._data?.errors

    if (errors && typeof errors === 'object') {
      for (const [key, messages] of Object.entries(errors)) {
        if (Array.isArray(messages) && messages.length) {
          if (knownFields.includes(key as T)) {
            setFieldError(key as T, messages[0])
          }
        }
      }
    }
  }

}

const onSubmit = form.handleSubmit(async (values) => {
  const requestBody: Record<string, unknown> = {
    organization_type: props.type,
    organization_id: props.id,
    last_name: values.last_name,
    first_name: values.first_name,
    middle_name: values.middle_name || '',
    phone: `+996${values.phone}`,
    email: values.email,
    message: values.message,
    fingerprint_id: useFingerprintStore().visitorId
  }

  const { data, error } = await useApiPost<{ message: string }>('/contacts-request', requestBody)
  if (error) {
    handleServerValidationErrors(error, form.setFieldError, [
      'last_name',
      'first_name',
      'middle_name',
      'email',
      'phone',
      'message'
    ])
  } else {
    trackEvent('generate_lead', {
      lead_type: 'org_contact',
      organization_id: props.id,
      organization_type: props.type
    })
    alertMessage.value = data?.message ?? ''
    alert.value = true
  }
})
</script>

<template>
  <ContentBlock
    :id="ANCHOR_IDS.FEATURE_BANK_PAGE_FORM_SUPPORT"
    :variant="props.variant"
    :heading-text="props.headingText"
    :heading-class="props.headingClass"
    :heading-tag="props.headingTag"
    :show-link-icon="true"
    class="FormScrollOffset"
  >
    <div>
      <Alert v-if="alert" variant="green">
        <AlertCircle class="h-4 w-4" />
        <AlertDescription>
          <div>{{ alertMessage }}</div>
        </AlertDescription>
      </Alert>
      <form
        v-else
        v-webmcp-tool="{
          name: 'contact_bank_support',
          description:
            'Send a request to the support service of this bank with the user\'s name and contacts.'
        }"
        class="flex flex-col gap-5"
        @submit="onSubmit"
      >
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField v-slot="{ componentField, errors }" name="last_name">
            <FormItem>
              <FormLabel>{{ $t('feature.bank_page_form_support.last_name_label') }}</FormLabel>
              <FormControl>
                <Input type="text" :placeholder="$t('common.last_name')" v-bind="componentField" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
          <FormField v-slot="{ componentField, errors }" name="first_name">
            <FormItem>
              <FormLabel>{{ $t('feature.bank_page_form_support.first_name_label') }}</FormLabel>
              <FormControl>
                <Input v-bind="componentField" :placeholder="$t('common.first_name')" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
          <FormField v-slot="{ componentField }" name="middle_name">
            <FormItem>
              <FormLabel>{{ $t('common.middle_name') }}</FormLabel>
              <FormControl>
                <Input v-bind="componentField" :placeholder="$t('common.middle_name')" />
              </FormControl>
            </FormItem>
          </FormField>
        </div>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField v-slot="{ componentField, errors }" name="phone">
            <FormItem class="md:flex-1">
              <FormLabel>{{ $t('feature.bank_page_form_support.phone_label') }}</FormLabel>
              <FormControl>
                <WidgetPhoneInput v-bind="componentField" :errors="errors.length" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
          <FormField v-slot="{ componentField, errors }" name="email">
            <FormItem class="md:flex-1">
              <FormLabel>{{ $t('feature.bank_page_form_support.email_label') }}</FormLabel>
              <FormControl>
                <Input type="email" v-bind="componentField" :placeholder="$t('common.email')" />
              </FormControl>
              <FormMessage v-if="errors.length" :message="errors[0]" />
            </FormItem>
          </FormField>
        </div>

        <FormField v-slot="{ componentField, errors }" name="message">
          <FormItem>
            <FormLabel>{{ $t('feature.bank_page_form_support.message_label') }}</FormLabel>
            <FormControl>
              <Textarea
                :placeholder="$t('feature.bank_page_form_support.message_placeholder')"
                v-bind="componentField"
              />
            </FormControl>
            <FormMessage v-if="errors.length" :message="errors[0]" />
          </FormItem>
        </FormField>
        <FormField v-slot="{ value, errorMessage, handleChange }" type="checkbox" name="agreement">
          <FormItem>
            <FormControl>
              <div class="flex items-center space-x-2">
                <Checkbox id="terms" :model-value="value" @update:model-value="handleChange" />
                <Label for="terms">
                  {{ $t('feature.bank_page_form_support.agreement_prefix') }}
                  <NuxtLinkLocale to="/privacy-policy" class="text-blue-600 underline">
                    {{ $t('feature.bank_page_form_support.agreement_link') }}
                  </NuxtLinkLocale>
                </Label>
              </div>
            </FormControl>
            <FormMessage v-if="errorMessage" :message="errorMessage" />
          </FormItem>
        </FormField>

        <div class="flex flex-col items-start gap-4 md:flex-row md:justify-between">
          <Button type="submit" class="mt-4 md:mt-0">{{
            $t('feature.bank_page_form_support.submit_button')
          }}</Button>
        </div>
      </form>
    </div>
  </ContentBlock>
</template>
